"use client";

import { useCallback, useEffect } from "react";
import {
  useUserPreferences,
  useUpdateUserPreferences,
} from "@/lib/user-preferences/use-user-preferences";

/**
 * Per-user saved-view tab-bar preferences (which pills are hidden, and their
 * left-to-right order), persisted server-side in `app_users.user_prefs`.
 *
 * Reads come from the SHARED app-wide preferences cache (`useUserPreferences`),
 * so navigating between entity pages does NOT refetch — the whole app loads the
 * user's preferences once and shares that cache. Writes go through the shared
 * optimistic mutation (`useUpdateUserPreferences`).
 *
 * This REPLACES the old per-browser `useHiddenViews` / `useViewOrder`
 * localStorage hooks. localStorage is per-browser/per-device and is wiped on
 * logout, so hidden tabs never followed the user — the bug this fixes. Storing
 * the preference against the user's account makes it survive logout and follow
 * them across browsers and devices, exactly like `seenTours` (see
 * USER_PREFERENCES.md).
 *
 * Both preferences are pure presentation: they change only which pills show and
 * in what order, never the views themselves or how filtering works.
 *
 * To avoid stranding existing users' hidden tabs, on first load (when the server
 * has no value for this entity yet) we do a one-time migration of any legacy
 * localStorage value up to the server, then remove the local key.
 */

const hiddenKeyFor = (entityName: string): string => `${entityName}-hidden-views`;
const orderKeyFor = (entityName: string): string => `${entityName}-view-order`;

interface UseSavedViewTabPrefsResult {
  hiddenIds: string[];
  isHidden: (viewId: string) => boolean;
  hideView: (viewId: string) => void;
  unhideView: (viewId: string) => void;
  orderedIds: string[];
  setOrder: (ids: string[]) => void;
  /** True once the shared preferences cache has loaded. */
  isHydrated: boolean;
}

/** Best-effort read of a legacy `string[]` localStorage value (migration only). */
function readLegacyList(key: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === "string")) {
      return parsed;
    }
  } catch {
    // Malformed / unavailable storage — ignore.
  }
  return null;
}

function clearLegacyKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage disabled — nothing to clean up.
  }
}

export const useSavedViewTabPrefs = (
  entityName: string | undefined
): UseSavedViewTabPrefsResult => {
  const { preferences, isHydrated } = useUserPreferences();
  const update = useUpdateUserPreferences();

  const hiddenIds = entityName
    ? (preferences.savedViewHiddenByEntity?.[entityName] ?? [])
    : [];
  const orderedIds = entityName
    ? (preferences.savedViewOrderByEntity?.[entityName] ?? [])
    : [];

  // One-time migration of any legacy per-browser value to the server, only when
  // the server has nothing yet. Idempotent: after migrating we clear the legacy
  // key and the optimistic cache update fills the server slot, so a re-run finds
  // nothing to migrate.
  useEffect(() => {
    if (!entityName || !isHydrated) return;
    const serverHidden = preferences.savedViewHiddenByEntity?.[entityName];
    const serverOrder = preferences.savedViewOrderByEntity?.[entityName];

    const legacyHidden =
      serverHidden === undefined ? readLegacyList(hiddenKeyFor(entityName)) : null;
    const legacyOrder =
      serverOrder === undefined ? readLegacyList(orderKeyFor(entityName)) : null;

    if (legacyHidden) {
      update({ setSavedViewHidden: { entityName, viewIds: legacyHidden } });
      clearLegacyKey(hiddenKeyFor(entityName));
    }
    if (legacyOrder) {
      update({ setSavedViewOrder: { entityName, viewIds: legacyOrder } });
      clearLegacyKey(orderKeyFor(entityName));
    }
  }, [entityName, isHydrated, preferences, update]);

  const hideView = useCallback(
    (viewId: string) => {
      if (!entityName || hiddenIds.includes(viewId)) return;
      update({
        setSavedViewHidden: { entityName, viewIds: [...hiddenIds, viewId] },
      });
    },
    [entityName, hiddenIds, update]
  );

  const unhideView = useCallback(
    (viewId: string) => {
      if (!entityName || !hiddenIds.includes(viewId)) return;
      update({
        setSavedViewHidden: {
          entityName,
          viewIds: hiddenIds.filter((id) => id !== viewId),
        },
      });
    },
    [entityName, hiddenIds, update]
  );

  const setOrder = useCallback(
    (ids: string[]) => {
      if (!entityName) return;
      update({ setSavedViewOrder: { entityName, viewIds: ids } });
    },
    [entityName, update]
  );

  const isHidden = useCallback(
    (viewId: string): boolean => hiddenIds.includes(viewId),
    [hiddenIds]
  );

  return {
    hiddenIds,
    isHidden,
    hideView,
    unhideView,
    orderedIds,
    setOrder,
    isHydrated,
  };
};
